import type { TonoEstado } from '../../utils/estado';

/**
 * Clases compartidas por las piezas del módulo. Los colores y medidas son
 * tokens de `tailwind.config.js` (prefijo `metas`), nunca hex sueltos.
 */

/**
 * Clase de TODA ventana emergente de PrimeReact del módulo (Dropdown, Calendar,
 * Dialog, ConfirmDialog, Toast, TieredMenu/Menu).
 *
 * PrimeReact pinta esas ventanas en `body`, FUERA de la raíz `.metas-ui`: sin
 * la clase, un Dropdown abierto o un diálogo salían con la fuente y los radios
 * del resto del panel. Va en `panelClassName` (Dropdown, Calendar) o en
 * `className` (Dialog, ConfirmDialog, Toast, menús).
 */
export const CLASE_OVERLAY = 'metas-ui';

/** Une clases, ignorando las vacías. */
export const cx = (...c: (string | false | null | undefined)[]): string => c.filter(Boolean).join(' ');

/** Tonos de las pastillas (`Pill`, `EstadoPill`, contadores de pestaña). */
export type TonoPill = TonoEstado;

export const CLASES_TONO: Record<TonoPill, string> = {
  neutro: 'bg-metas-neutro-fondo text-metas-neutro',
  info: 'bg-metas-info-fondo text-metas-info',
  exito: 'bg-metas-exito-fondo text-metas-exito',
  error: 'bg-metas-error-fondo text-metas-error',
  aviso: 'bg-metas-aviso-fondo text-metas-aviso',
};

/**
 * Caja de un campo propio (input o select nativo dentro de un borde), como el
 * `.field` del diseño: normal, en aviso (fila pendiente: borde ámbar y anillo)
 * o apagado (meta con motivo, motivo con meta).
 */
export type EstadoCampo = 'normal' | 'aviso' | 'off';

export const CLASE_CAMPO_BASE =
  'flex h-metas-control items-center gap-2 rounded-metas-control border bg-metas-panel px-3 text-sm text-metas-texto';

export const CLASES_CAMPO: Record<EstadoCampo, string> = {
  normal: `${CLASE_CAMPO_BASE} border-metas-borde-control`,
  aviso: `${CLASE_CAMPO_BASE} border-metas-campo-aviso shadow-metas-anillo-aviso`,
  off: `${CLASE_CAMPO_BASE} border-metas-borde-control !bg-metas-campo-off text-metas-texto-2`,
};

/*
 * «44 en teléfono» = `max-md:` (por ancho) Y `[@media(pointer:coarse)]:` (pantalla
 * táctil del ancho que sea): a 768 una tableta perdía lo táctil (auditoría del
 * 22-oct-2026). Las clases van escritas enteras: Tailwind sólo genera las que lee.
 */

/** Botón de sólo icono (anterior / siguiente, menú ⋮): 40 px, 44 en teléfono o pantalla táctil. */
export const CLASE_BOTON_ICONO =
  'metas-foco inline-flex h-metas-control w-metas-control shrink-0 items-center justify-center rounded-metas-control border border-metas-borde-control bg-metas-panel text-metas-texto-3 hover:bg-metas-cabecera disabled:cursor-not-allowed disabled:opacity-50 max-md:h-metas-tactil max-md:w-metas-tactil [@media(pointer:coarse)]:h-metas-tactil [@media(pointer:coarse)]:w-metas-tactil';

/** Panel blanco con borde y radio 12. */
export const CLASE_PANEL = 'rounded-metas-panel border border-metas-borde bg-metas-panel';

/** Botón del diseño (`.btn`): 40 px, borde gris, fondo blanco; 44 en teléfono. */
export const CLASE_BOTON =
  'metas-foco inline-flex h-metas-control shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-metas-control border border-metas-borde-control bg-metas-panel px-4 text-sm font-semibold text-metas-texto hover:bg-metas-cabecera disabled:cursor-not-allowed disabled:opacity-50 max-md:h-metas-tactil [@media(pointer:coarse)]:h-metas-tactil';

/** Botón primario del diseño (`.btn-primary`): azul; deshabilitado, azul claro (no transparente). */
export const CLASE_BOTON_PRIMARIO =
  'metas-foco inline-flex h-metas-control shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-metas-control border border-metas-primario bg-metas-primario px-4 text-sm font-semibold text-white hover:border-metas-primario-hover hover:bg-metas-primario-hover disabled:cursor-not-allowed disabled:border-metas-primario-off disabled:bg-metas-primario-off max-md:h-metas-tactil [@media(pointer:coarse)]:h-metas-tactil';

/** Botón de texto (`.btn-link`). */
export const CLASE_BOTON_TEXTO =
  'metas-foco inline-flex h-metas-control shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-metas-control px-2 text-sm font-semibold text-metas-primario hover:bg-metas-cabecera disabled:cursor-not-allowed disabled:opacity-50 max-md:h-metas-tactil [@media(pointer:coarse)]:h-metas-tactil';
